import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material'; // Import MUI icons
import { useAuth } from '../context/AuthContext';
import api from '../api';

const LoginPage = () => {
  const { login, isAuthenticated, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ identifier: '', password: '' });

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      if (['admin', 'super-admin', 'co-admin'].includes(user.role)) {
        navigate('/admin');
      } else {
        navigate('/home');
      }
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [twoFactor, setTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [receivedTwoFactorCode, setReceivedTwoFactorCode] = useState('');
  const [showPassword, setShowPassword] = useState(false); // State for password visibility

  const { identifier, password } = formData;

  const onChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/auth/login', formData);

      // DIRECT LOGIN (2FA BYPASSED)
      login(response.data.token, response.data.user);

      if (response.data.user && ['admin', 'super-admin', 'co-admin'].includes(response.data.user.role)) {
        window.location.href = '/admin';
      } else {
        window.location.href = '/home';
      }

      /* 2FA DISABLED
      setTwoFactor(true);
      if (response.data.twoFactorCode) {
        setReceivedTwoFactorCode(response.data.twoFactorCode);
      }
      */
    } catch (error) {
      setError(error.response ? error.response.data.msg : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Verify 2FA using identifier (email or mobile)
      const response = await api.post('/auth/verify-2fa', { email: identifier, twoFactorCode });


      // Use the login function from AuthContext
      login(response.data.token, response.data.user);

      // Role-Based Redirect
      if (response.data.user && ['admin', 'super-admin', 'co-admin'].includes(response.data.user.role)) {
        window.location.href = '/admin';
      } else {
        window.location.href = '/home';
      }
    } catch (error) {
      setError(error.response ? error.response.data.msg : 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      {/* 1. Logo & Header */}
      <div className="auth-header">
        <img src={require('../assets/logo_full.webp')} alt="MAB Jewels" className="auth-brand-logo" />
        <h1 className="auth-brand-title">MAB Jewels</h1>
        <p className="auth-brand-subtitle">Trusted Jewellery Management</p>
      </div>

      {/* 2. The Vault Card */}
      <div className="auth-card">
        <div className="auth-card-header">
          <h2 className="auth-card-title">Welcome Back</h2>
          <p className="auth-card-subtitle">Sign in</p>
        </div>

        {error && (
          <div className="auth-alert">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} noValidate>
          {/* Email */}
          <div className="auth-form-group">
            <label htmlFor="identifier" className="auth-label">Email or Mobile Number</label>
            <div className="auth-input-wrapper">
              <input
                type="text"
                id="identifier"
                name="identifier"
                className="auth-input"
                value={identifier}
                onChange={onChange}
                placeholder="Email or 10-digit mobile"
                required
                autoComplete="username"
                disabled={loading}
              />
            </div>
          </div>

          {/* Password */}
          <div className="auth-form-group">
            <label htmlFor="password" className="auth-label">Password</label>
            <div className="auth-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                className="auth-input"
                value={password}
                onChange={onChange}
                placeholder="Enter your secure password"
                required
                autoComplete="current-password"
                disabled={loading}
                style={{ paddingRight: '45px' }}
              />
              <button
                type="button"
                className="form-input-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
              >
                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn-vault"
            disabled={loading || !identifier || !password}
          >
            {loading ? 'Authenticating...' : 'Sign In'}
          </button>

          <div className="auth-footer">
            <Link to="/forgot-password" className="auth-link">
              Forgot your password?
            </Link>
            <div style={{ marginTop: '16px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
              Don't have an account?{' '}
              <Link to="/register" className="auth-link" style={{ fontWeight: '700' }}>
                Create one here
              </Link>
            </div>
          </div>
        </form>
      </div>

      {/* Footer Info */}
      <div style={{ textAlign: 'center', marginTop: '30px', color: '#888', fontSize: '0.8rem' }}>
        <p>&copy; {new Date().getFullYear()} MAB Jewels. Secure Portal.</p>
      </div>
    </div>
  );
};

export default LoginPage;
